/**
 * LosiRealtimeAura
 *
 * Realtime voice face: Losi silhouette + eyes, with the real WebGL Aura Field
 * stacked underneath (not a circular VoiceOrb, not a lazy stroke glow).
 *
 * Pass `levelRef` (0..1, negative = procedural) for zero-rerender audio wiring —
 * from useAudioLevel, createLiveKitAdapter, or useLosiInferenceVoice.levelRef.
 */

'use client'

import { useMemo, useRef, type RefObject } from 'react'
import { useTheme } from 'next-themes'

import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import type { LosiInferenceAgentUiState } from '@/hooks/useLosiInferenceVoice'
import {
  LOSI_AURA_FROM,
  LOSI_AURA_TO,
  realtimeAuraColors
} from '@/lib/chat/realtime-aura-colors'
import type { ExpressionId } from '@/lib/losi-engine/expressions'
import { DEFAULT_SHAPE } from '@/lib/losi-engine/skins'
import type { StateId } from '@/lib/losi-engine/states'
import { cn } from '@/lib/utils'
import type { OrbProps, OrbState } from '@/registry/lib/orb-state'
import { OrbStatus } from '@/registry/lib/orb-status'
import { AuraField } from '@/registry/orbe/aura-field/aura-field'

export { LOSI_AURA_FROM, LOSI_AURA_TO, realtimeAuraColors }

export type LosiRealtimeAuraProps = {
  /** LiveKit / inference agent phase */
  agentState: LosiInferenceAgentUiState
  connecting?: boolean
  muted?: boolean
  /** Optional explicit OrbState override (gallery / demos). */
  state?: OrbState
  /**
   * Preferred: live amplitude ref (0..1). Negative = procedural energy.
   * Avoids re-rendering on every audio sample.
   */
  levelRef?: RefObject<number>
  /** Legacy: number level (causes parent re-renders if updated every frame). */
  level?: number
  size?: number
  speed?: number
  /** Optional fixed colours — when omitted, listening/speaking (etc.) pick pairs. */
  colorFrom?: string
  colorTo?: string
  shape?: string
  className?: string
  showStatus?: boolean
}

/** Map session flags → Aura Field OrbState. */
export function deriveRealtimeOrbState({
  connecting,
  muted,
  agentState
}: {
  connecting?: boolean
  muted?: boolean
  agentState: LosiInferenceAgentUiState
}): OrbState {
  if (connecting) return 'connecting'
  if (muted) return 'disabled'
  switch (agentState) {
    case 'listening':
      return 'listening'
    case 'thinking':
      return 'thinking'
    case 'speaking':
      return 'speaking'
    case 'asleep':
      return 'disabled'
    default:
      return 'idle'
  }
}

/** Losi engine pose for each orb state — expressions only on baseFace (`idle`). */
export function losiPoseForOrbState(state: OrbState): {
  state: StateId
  expression: ExpressionId | null
} {
  switch (state) {
    case 'connecting':
      return { state: 'agentConnect', expression: null }
    case 'thinking':
      return { state: 'thinking', expression: null }
    case 'listening':
      return { state: 'idle', expression: 'attentif' }
    case 'speaking':
      return { state: 'idle', expression: 'heureux' }
    case 'error':
      return { state: 'idle', expression: 'effraye' }
    case 'disabled':
      return { state: 'idle', expression: 'somnolent' }
    default:
      return { state: 'idle', expression: 'neutre' }
  }
}

export function LosiRealtimeAura({
  agentState,
  connecting = false,
  muted = false,
  state: stateOverride,
  levelRef: levelRefProp,
  level,
  size = 56,
  speed = 1,
  colorFrom,
  colorTo,
  shape = DEFAULT_SHAPE,
  className,
  showStatus = true
}: LosiRealtimeAuraProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const fallbackLevelRef = useRef(-1)
  if (!levelRefProp) {
    fallbackLevelRef.current = typeof level === 'number' ? level : -1
  }
  const levelRef = levelRefProp ?? fallbackLevelRef

  const { resolvedTheme } = useTheme()
  const clair = resolvedTheme === 'light'

  const orbState =
    stateOverride ??
    deriveRealtimeOrbState({ connecting, muted, agentState })

  const pose = useMemo(() => losiPoseForOrbState(orbState), [orbState])
  const palette = useMemo(() => {
    if (colorFrom && colorTo) return { from: colorFrom, to: colorTo }
    return realtimeAuraColors(orbState, clair)
  }, [colorFrom, colorTo, orbState, clair])

  const label = muted
    ? 'Losi muted'
    : connecting
      ? 'Losi connecting'
      : `Losi ${orbState}`

  return (
    <div
      ref={hostRef}
      className={cn(
        'relative inline-flex flex-col items-center justify-center gap-1',
        className
      )}
      data-state={orbState}
      role="img"
      aria-label={label}
    >
      <div
        className="relative"
        style={{ width: size, height: size }}
      >
        <div
          className="pointer-events-none absolute inset-0 z-0 grid place-items-center"
          aria-hidden
        >
          <AuraField
            state={orbState}
            levelRef={levelRef}
            size={size}
            speed={speed}
            colorFrom={palette.from}
            colorTo={palette.to}
            label={label}
          />
        </div>
        <div className="relative z-[1]">
          <LosiAvatar
            state={pose.state}
            expression={pose.expression}
            shape={shape}
            size={size}
            fitBody
            title={label}
          />
        </div>
      </div>
      {showStatus ? (
        <OrbStatus
          state={orbState}
          className="text-muted-foreground text-[11px] font-medium tracking-wide"
        />
      ) : null}
    </div>
  )
}

export type { OrbProps, OrbState }
